import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Mail, MapPin, Phone, Loader2 } from "lucide-react";
import { CONTACT, SERVICE_OPTIONS, revealUp } from "../../lib/mrs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { name: "", phone: "", email: "", service: "", message: "" };

const ROTATING_WORDS = ["Construction", "Interiors", "Exteriors", "Real Estate"];

const RotatingWords = () => {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(
      () => setIndex((i) => (i + 1) % ROTATING_WORDS.length),
      2200
    );
    return () => clearInterval(id);
  }, []);

  return (
    <span className="relative inline-block overflow-hidden align-baseline h-[0.9em] min-w-[4ch]">
      <AnimatePresence mode="wait">
        <motion.span
          key={index}
          initial={{ y: "110%" }}
          animate={{ y: "0%" }}
          exit={{ y: "-110%" }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="block text-[#b89b72] whitespace-nowrap"
        >
          {ROTATING_WORDS[index]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
};

export const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Thank you — we'll be in touch shortly.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Something went wrong. Please try WhatsApp instead.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#eae7e0]/20 py-3 text-[#eae7e0] placeholder:text-[#9ca399]/60 focus:outline-none focus:border-[#b89b72] transition-colors duration-300";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#0a0b0a] pt-24 md:pt-32 lg:pt-40 px-6 md:px-12 lg:px-20 border-t border-[#eae7e0]/10"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
        {/* Left: info */}
        <div className="lg:col-span-5">
          <motion.p
            variants={revealUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-[11px] md:text-sm tracking-[0.28em] uppercase text-[#b89b72] mb-5"
          >
            Let&apos;s Talk
          </motion.p>
          <motion.h2
            variants={revealUp}
            custom={1}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="font-display font-light text-[#eae7e0] leading-[1.05] text-4xl md:text-5xl lg:text-6xl tracking-tight mb-10"
          >
            Let&apos;s bring a piece of nature into your space.
          </motion.h2>

          <div className="space-y-6">
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-button"
              className="group flex items-center gap-4 w-full sm:w-auto px-6 py-4 bg-[#b89b72] text-[#0a0b0a] hover:bg-[#cbb38e] transition-colors duration-300"
            >
              <MessageCircle size={20} />
              <span className="text-[12px] tracking-[0.2em] uppercase">
                WhatsApp Us Now
              </span>
            </a>

            <a
              href={`mailto:${CONTACT.email}`}
              data-testid="contact-email-link"
              className="flex items-start gap-4 text-[#9ca399] hover:text-[#eae7e0] transition-colors duration-300"
            >
              <Mail size={18} className="mt-1 text-[#b89b72]" />
              <span className="text-base md:text-lg break-all">{CONTACT.email}</span>
            </a>

            <div className="flex items-start gap-4 text-[#9ca399]">
              <Phone size={18} className="mt-1 text-[#b89b72]" />
              <span className="text-base md:text-lg">{CONTACT.phone}</span>
            </div>

            <div className="flex items-start gap-4 text-[#9ca399]">
              <MapPin size={18} className="mt-1 text-[#b89b72]" />
              <span className="text-base md:text-lg">
                {CONTACT.locations.join(" · ")}
              </span>
            </div>
          </div>
        </div>

        {/* Right: form */}
        <div className="lg:col-span-7">
          <motion.form
            variants={revealUp}
            custom={2}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#9ca399] mb-2 block">
                Name *
              </label>
              <input
                data-testid="contact-input-name"
                value={form.name}
                onChange={update("name")}
                placeholder="Your name"
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#9ca399] mb-2 block">
                Phone *
              </label>
              <input
                data-testid="contact-input-phone"
                value={form.phone}
                onChange={update("phone")}
                placeholder="+91"
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#9ca399] mb-2 block">
                Email
              </label>
              <input
                data-testid="contact-input-email"
                value={form.email}
                onChange={update("email")}
                placeholder="you@email.com"
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#9ca399] mb-2 block">
                Service
              </label>
              <select
                data-testid="contact-select-service"
                value={form.service}
                onChange={update("service")}
                className={`${inputCls} appearance-none cursor-pointer`}
              >
                <option value="" className="bg-[#121412]">
                  Select a service
                </option>
                {SERVICE_OPTIONS.map((s) => (
                  <option key={s} value={s} className="bg-[#121412]">
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs tracking-[0.2em] uppercase text-[#9ca399] mb-2 block">
                Message
              </label>
              <textarea
                data-testid="contact-input-message"
                value={form.message}
                onChange={update("message")}
                rows={3}
                placeholder="Tell us about your space & vision"
                className={`${inputCls} resize-none`}
              />
            </div>
            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-form-submit"
                className="group inline-flex items-center gap-3 px-10 py-4 bg-[#b89b72] text-[#0a0b0a] text-[12px] tracking-[0.2em] uppercase hover:bg-[#cbb38e] transition-colors duration-300 disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Sending
                  </>
                ) : (
                  <>
                    Book Free Consultation
                    <span className="transition-transform duration-300 group-hover:translate-x-1">
                      →
                    </span>
                  </>
                )}
              </button>
            </div>
          </motion.form>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-24 md:mt-32">
        <motion.h3
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          data-testid="footer-brand"
          className="font-display font-light text-[#eae7e0] leading-[0.95] tracking-tight text-center whitespace-nowrap flex items-baseline justify-center gap-3 md:gap-5 text-4xl sm:text-5xl md:text-7xl lg:text-8xl"
        >
          <span>MRS</span>
          <span className="italic text-[#eae7e0]/90">
            <RotatingWords />
          </span>
        </motion.h3>
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 border-t border-[#eae7e0]/10 py-8 mt-10 md:mt-16">
          <p className="text-xs tracking-wide text-[#9ca399]">
            © {new Date().getFullYear()} MRS Builders. Where Vision Becomes
            Structure.
          </p>
          <p className="text-xs tracking-[0.2em] uppercase text-[#9ca399]">
            Bangalore · Mangalore
            <a
              href="/admin"
              data-testid="footer-admin-link"
              className="ml-4 text-[#9ca399]/50 hover:text-[#b89b72] transition-colors normal-case tracking-normal"
            >
              Admin
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};
